import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Mail, MessageSquare, FileText, Download, TrendingUp, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API_BASE_URL = '/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/stats`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const cards = [
    { icon: Users, label: 'Total Leads', value: stats?.totalLeads || 0, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/30' },
    { icon: Mail, label: 'Subscribers', value: stats?.totalSubscribers || 0, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-950/30' },
    { icon: MessageSquare, label: 'Contact Messages', value: stats?.totalContacts || 0, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/30' },
    { icon: FileText, label: 'Blog Posts', value: stats?.totalBlogPosts || 0, color: 'text-purple-500', bg: 'bg-purple-950/30' },
  ];

  const handleExport = async (type: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/export/${type}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-display">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of your OyinBatfo admin data</p>
        </div>
        <div className="flex gap-2">
          {['leads', 'subscribers', 'contacts'].map((type) => (
            <Button
              key={type}
              variant="outline"
              size="sm"
              onClick={() => handleExport(type)}
            >
              <Download className="w-4 h-4 mr-1" /> Export {type}
            </Button>
          ))}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <Card key={card.label} className="border-2 border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">{card.label}</p>
                  <p className="text-3xl font-bold mt-1">{card.value.toLocaleString()}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${card.bg} flex items-center justify-center`}>
                  <card.icon className={`w-6 h-6 ${card.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lead Type Breakdown */}
      {stats?.leadsByType && Object.keys(stats.leadsByType).length > 0 && (
        <Card className="border-2 border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Lead Magnet Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.entries(stats.leadsByType).map(([type, count]) => (
                <div key={type} className="bg-muted/50 rounded-xl p-5">
                  <p className="text-sm text-muted-foreground capitalize">
                    {type === 'checklist' ? '📋 School Uniform Checklist' : '📊 ROI Calculator'}
                  </p>
                  <p className="text-2xl font-bold mt-1">{(count as number).toLocaleString()} downloads</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card className="border-2 border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" />
            Recent Downloads
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recentDownloads && stats.recentDownloads.length > 0 ? (
            <div className="space-y-3">
              {stats.recentDownloads.map((lead: any) => (
                <div key={lead.id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                      {lead.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{lead.name}</p>
                      <p className="text-xs text-muted-foreground">{lead.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      lead.magnet_type === 'checklist'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                        : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    }`}>
                      {lead.magnet_type === 'checklist' ? 'Checklist' : 'ROI Calc'}
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Download className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No downloads yet. Share your lead magnets to get started!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
